import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Handles the two interactive actions inside the Offers Center dialog:
 * copying a promo code, and applying one. The dialog itself (open/close,
 * backdrop click, Escape, focus trap, scroll lock) is entirely owned by
 * dialog-component (dialog.js) - this component only reacts to content
 * inside it. Its open/closed state survives a cart re-render for free:
 * morph.js already preserves the `open` attribute on <dialog> elements the
 * same way it does for <details>, so no local state-tracking is needed here.
 *
 * Applying deliberately does not duplicate cart-discount.js's fetch/morph
 * logic - it fills the existing #cart-discount input (rendered by
 * cart-summary.liquid) and submits its existing form, so the already-wired
 * <cart-discount-component> handles the request, success pill, and error
 * states exactly as it does for a manually-typed code.
 *
 * @typedef {Object} CartOffersRefs
 * @property {HTMLElement} feedback - The aria-live status element for copy feedback.
 *
 * @extends {Component<CartOffersRefs>}
 */
class CartOffers extends Component {
  requiredRefs = ['feedback'];

  /** @type {number | undefined} */
  #copyResetTimeout;

  /**
   * Element to restore focus to when the Offers Center closes - dialog.js's
   * dialog-component doesn't track this itself (unlike theme-drawer.js,
   * which handles the same problem for the cart drawer). Mirrors that
   * existing pattern, including the same fallback: a Section Rendering API
   * morph can replace the trigger node while the dialog is open, so a stale
   * reference is verified against the live DOM before use.
   * @type {HTMLElement | null}
   */
  #previouslyFocused = null;

  get #dialogComponent() {
    return this.querySelector('dialog-component');
  }

  /**
   * Fallback focus target when #previouslyFocused was lost (detached by a
   * re-render). Looked up by `aria-controls` pointing at this dialog's id
   * rather than a fixed class, since there can be two valid triggers - the
   * one inside this component's own subtree (.cart-offers-trigger) and, when
   * settings.cart_offers_trigger_placement includes "sticky", the compact
   * one rendered separately inside cart-sticky-checkout.liquid.
   */
  get #trigger() {
    const dialogId = this.#dialogComponent?.id;
    return dialogId ? document.querySelector(`[aria-controls="${dialogId}"]`) : null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountSettled);
    // dialog:close doesn't bubble, so this listens on dialog-component
    // directly rather than relying on delegation.
    this.#dialogComponent?.addEventListener('dialog:close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountSettled);
  }

  /**
   * Opens the Offers Center. Captures the currently-focused element (the
   * trigger button itself, since clicking it already focused it) before
   * calling showDialog() - showDialog() moves focus to the dialog
   * synchronously, so capturing via a `dialog:open` listener (which fires
   * after that shift) would record the dialog's own close button instead.
   */
  openOffersCenter = () => {
    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialogComponent = /** @type {any} */ (this.#dialogComponent);
    dialogComponent?.showDialog();
  };

  #handleDialogClose = () => {
    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      this.#trigger?.focus();
    }
  };

  /**
   * A successful apply re-renders this whole section (resetting the button
   * for free), but a rejected code only updates cart-discount-component's
   * own error area - nothing else re-renders. Without this, the button this
   * component itself disabled would stay stuck on "Applying…" forever.
   * @param {InstanceType<import('@shopify/events').CartDiscountUpdateEvent>} event
   */
  #handleDiscountSettled = (event) => {
    /** @type {Promise<unknown> | undefined} */
    const promise = /** @type {any} */ (event).promise;
    promise?.finally(() => {
      for (const button of this.querySelectorAll('.cart-offers__code-apply')) {
        if (!(button instanceof HTMLButtonElement) || !button.disabled) continue;
        button.disabled = false;
        const label = button.querySelector('.cart-offers__code-apply-label');
        if (label && button.dataset.ctaLabel) {
          label.textContent = button.dataset.ctaLabel;
        }
      }
    });
  };

  /**
   * Copies a discount code to the clipboard.
   * @param {MouseEvent} event
   */
  copyCode = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLElement)) return;

    const code = button.dataset.code;
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    button.dataset.copied = 'true';
    this.refs.feedback.textContent = this.dataset.copiedLabel || code;

    window.clearTimeout(this.#copyResetTimeout);
    this.#copyResetTimeout = window.setTimeout(() => {
      delete button.dataset.copied;
    }, 1600);
  };

  /**
   * Applies a discount code by delegating to the existing discount form.
   * The clicked button is disabled with a temporary "Applying…" label,
   * restored by #handleDiscountSettled once the request resolves (a
   * successful apply also re-renders this section from fresh server data,
   * which naturally supersedes it).
   * @param {MouseEvent} event
   */
  applyCode = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLElement)) return;

    const code = button.dataset.code;
    if (!code) return;

    const input = document.getElementById('cart-discount');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = code;
    const form = input.closest('form');
    if (form instanceof HTMLFormElement) {
      form.requestSubmit();
    }

    const label = button.querySelector('.cart-offers__code-apply-label');
    if (label && button.dataset.applyingLabel) {
      label.textContent = button.dataset.applyingLabel;
    }
    button.disabled = true;
  };
}

if (!customElements.get('cart-offers-component')) {
  customElements.define('cart-offers-component', CartOffers);
}
